import { getPathWithParams } from '@/components/InstantSearch/util/url-search-params';
import {
  type ALL_CAMPAIGNS_SEARCH_PARAM_KEYS,
  type CAMPAIGNS_SORT_KEYS,
  type CampaignsSearchParamType,
} from '@/router/searchParams/campaignSearchParams';
import { type NavigableRouteType, type RouteSearchParamEnumType } from '@/router/types';

import { instance } from './appRoutes';

export const campaigns = {
  path: `${instance.path}/campaigns`,
  description:
    'Displays metrics for each campaign. An overview of each campaign is shown with the channel and vendor it belongs to, total spend, pixel impressions, pixel click-throughs, visits, companies who have engaged with the campaign, pipeline opportunities influenced, and closed-won opportunities influenced. The campaign can be broken down by the audiences that are targeted through that campaign. A fit score visualization is also displayed which shows how well the campaign is performing relative to other campaigns in the same space.',
  navigable: true,
  pathParams: {},
  searchParams: {
    'c.campaignId': {
      type: 'string[]',
      description: 'A comma-separated string of campaign IDs',
      requiresLookup: true,
      lookupType: 'campaign',
    },
    'c.audienceId': {
      type: 'string',
      description: 'An audience ID to filter the channels by',
      requiresLookup: true,
      lookupType: 'audience',
    },
    'c.vendor.id': {
      type: 'string[]',
      description: 'A comma-separated string of vendor IDs',
      requiresLookup: true,
      lookupType: 'vendor',
    },
    'c.spend': {
      type: 'range',
      description: 'A range of ad spend from a min to max value.',
    },
    'c.spendPerTargetVisit': {
      type: 'range',
      description: 'A range of cost per target visit from a min to max value.',
    },
    'c.adImpressions': {
      type: 'range',
      description: 'A range of ad impressions from a min to max value.',
    },
    'c.adClicks': {
      type: 'range',
      description: 'A range of ad clicks from a min to max value.',
    },
    'c.visits': {
      type: 'range',
      description: 'A range of visits from a min to max value.',
    },
    'c.viewThroughPercent': {
      type: 'range',
      description:
        'A range of view-through percentage from a min to max value. This is a percentage field which can range from 0 to 1.',
    },
    'c.visitEfficiency': {
      type: 'range',
      description:
        'A range of visit efficiency from a min to max value. This is a percentage field which can range from 0 to 1.',
    },
    'c.viewThroughVisits': {
      type: 'range',
      description: 'A range of view-through visits from a min to max value.',
    },
    'c.engagedCompanies': {
      type: 'range',
      description: 'A range of engaged companies from a min to max value.',
    },
    'c.companiesReached': {
      type: 'range',
      description: 'A range of companies reached from a min to max value.',
    },
    'c.leadsInfluenced': {
      type: 'range',
      description: 'A range of leads influenced from a min to max value.',
    },
    'c.pipelineInfluenced': {
      type: 'range',
      description:
        'A range of pipeline influenced from a min to max value. This is a currency field which indicates the amount of possible revenue influenced by the campaign.',
    },
    'c.countPipelineInfluenced': {
      type: 'range',
      description:
        'A range of pipeline opportunities influenced from a min to max value. This is a count field.',
    },
    'c.closedWonInfluenced': {
      type: 'range',
      description:
        'A range of closed-won influenced from a min to max value. This is a currency field which indicates the amount of revenue influenced by the campaign that has been closed as won.',
    },
    'c.countClosedwonInfluenced': {
      type: 'range',
      description:
        'A range of closed-won opportunities influenced from a min to max value. This is a count field.',
    },
    'c.customImpactScore': {
      type: 'range',
      description: 'A range of impact score from a min to max value.',
    },
    'c.opportunityInfluencedSumPipelineCustom': {
      type: 'range',
      description: 'A range of impact score pipeline attribution from a min to max value.',
    },
    'c.opportunityInfluencedSumClosedWonCustom': {
      type: 'range',
      description: 'A range of impact score closed won attribution from a min to max value.',
    },
    'c.sortBy': {
      type: 'enum',
      enumValues: [
        'adClicks',
        'adImpressions',
        'campaign.name',
        'closedWonFirstTouchAttribution',
        'closedWonInfluenced',
        'closedWonLinearAttribution',
        'companiesReached',
        'countClosedwonInfluenced',
        'countPipelineInfluenced',
        'customImpactScore',
        'engagedCompanies',
        'leadsInfluenced',
        'pipelineFirstTouchAttribution',
        'pipelineInfluenced',
        'pipelineLastTouchAttribution',
        'opportunityInfluencedSumClosedWonCustom',
        'opportunityInfluencedSumPipelineCustom',
        'pipelineLinearAttribution',
        'spend',
        'spendPerTargetVisit',
        'vendor.id',
        'viewThroughPercent',
        'viewThroughVisits',
        'visitEfficiency',
        'visits',
      ],
      default: 'visits',
      description: 'The field to sort by. Only one field can be sorted at a time.',
    } satisfies RouteSearchParamEnumType<CAMPAIGNS_SORT_KEYS>,
    'c.sortOrder': {
      type: 'enum',
      enumValues: ['asc', 'desc'],
      default: 'desc',
      description:
        'The sort order. Fields with type `number` or `date` are usually best sorted in descending order. While string fields are best sorted in ascending order.',
    },
    'c.page': {
      type: 'string',
      description: 'The page number to display',
    },
    'c.size': {
      type: 'string',
      description: 'The number of items to display per page',
    },
  },
} satisfies NavigableRouteType<ALL_CAMPAIGNS_SEARCH_PARAM_KEYS>;

export const campaignDetail = {
  path: `${instance.path}/campaigns/:campaigns`,
  description:
    'Display metrics for a single campaign or a comma-separated list of campaign ids as a dynamic grouped campaign. The campaign is shown with the total number of visits over time, return on marketing spend (ROMS), visit efficiency, and campaign influence on pipeline and closed-won opportunities. The campaign can be broken down by an audience of companies.',
  navigable: true,
  pathParams: {
    campaigns: {
      type: 'string[]',
      description: 'The IDs of the campaigns the user wishes to view',
      requiresLookup: true,
      lookupType: 'campaign',
      required: true,
    },
  },
  searchParams: {
    audience: {
      type: 'string',
      description: 'An audience ID to filter the campaigns by',
      requiresLookup: true,
      lookupType: 'audience',
      required: false,
    },
  },
} satisfies NavigableRouteType<'audience'>;

export const campaignById = (campaignId?: string) =>
  campaignId && `${campaigns.path}/${encodeURIComponent(campaignId)}`;
export const campaignGroupByIds = (campaignIds: string[]) =>
  `${campaigns.path}/${campaignIds.join(',')}`;

/**
 * Returns a URL string for the web traffic activity route with the given search params.
 *
 * @param params An object with keys that match the search params for the web traffic activity
 * route. Undefined values are skipped.
 * @returns A URL string with the search params appended as query params
 */
export const campaignsWithParams = (params: Partial<CampaignsSearchParamType>) => {
  return getPathWithParams(campaigns.path, params);
};
